import {useGeocoder} from "./useGeocoder";
import {useEffect, useMemo, useState} from "react";
import {getQueryParamsFromPlace} from "../../util/AddressUtils";

type GeocoderResult = google.maps.GeocoderResult;

export const useGeocodeByAddress = ({address, region}: {address: string | undefined, region?: string}) => {
    const { loading: geocoderLoading, geocoder } = useGeocoder();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Kept with the address they answer, so a new address never sees the previous
    // one's place or failure while it loads (e.g. Back to another city, securspace-ui#3053).
    const [geocoded, setGeocoded] = useState<{address: string, place: GeocoderResult}>();
    const [failure, setFailure] = useState<{address: string, error: Error | boolean}>();

    const geocodingLoading = useMemo(() => {
        return geocoderLoading || isLoading || !geocoder?.geocode
    }, [geocoder, geocoderLoading, isLoading])

    useEffect(() => {
        let isCancelled = false;

        if (address && geocoder?.geocode && !geocoderLoading) {
            setIsLoading(true);
            setFailure(undefined);
            // If a user navigates to this page via URL only
            geocoder.geocode({ address, region }).then(({ results: places }: google.maps.GeocoderResponse) => {
                if (isCancelled) return;
                if (places && places.length > 0) {
                    const place = places[0];
                    // Validate that geocoded result contains required city and state components
                    const addressParts = getQueryParamsFromPlace(place);
                    if (addressParts?.city && addressParts?.state) {
                        setGeocoded({address, place: {...place}});
                    } else {
                        setFailure({address, error: new Error('Geocoded address missing city or state')});
                    }
                } else {
                    setFailure({address, error: new Error('No geocode results')});
                    setIsLoading(false);
                }
            }).catch((error: any) => {
                if (!isCancelled) setFailure({address, error})
            }).finally(() => {
                if (!isCancelled) setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }

        return () => { isCancelled = true; };
    }, [address, region, geocoder, geocoderLoading]);

    return {
        geocodedAddress: geocoded && geocoded.address === address ? geocoded.place : undefined,
        geocodingFailed: failure && failure.address === address ? failure.error : undefined,
        geocodingLoading
    }
}
